class SfCollectionQuickview extends HTMLElement {
  connectedCallback() {
    this._initQuickView();
    this._bindAddToBag();
  }

  disconnectedCallback() {
    if (this._onKeyDown) document.removeEventListener('keydown', this._onKeyDown);
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    document.removeEventListener('click', this._handleContentClick, true);
    document.removeEventListener('change', this._handleContentChange, true);
  }

  _bindAddToBag() {
    this.addEventListener('sbs:open-quickview', (e) => {
      // Stop bubbling so only the closest <sf-collection-quickview> opens a drawer.
      e.stopPropagation();
      const detail = e.detail || {};
      if (detail.handle) this._openQuickView(detail.handle, detail.url, detail.variantId);
    });
  }

  _initQuickView() {
    this._quickview = this.querySelector('.ec-quickview');
    console.log('[SF-QV][2] _initQuickView: .ec-quickview found =', !!this._quickview, this._quickview);
    if (!this._quickview) {
      console.warn('[SF-QV][2x] no .ec-quickview inside this <sf-collection-quickview>, aborting init');
      return;
    }

    this._qvDrawer = this._quickview.querySelector('.ec-quickview__drawer');
    this._qvContent = this._quickview.querySelector('.ec-quickview__content');
    this._qvLoading = this._quickview.querySelector('.ec-quickview__loading');
    this._qvOverlay = this._quickview.querySelector('.ec-quickview__overlay');

    console.log('[SF-QV][3] refs', {
      drawer: !!this._qvDrawer,
      content: !!this._qvContent,
      loading: !!this._qvLoading,
      overlay: !!this._qvOverlay,
    });

    // Per-open state — refreshed every time a product is loaded.
    this._productData = null;
    this._selectedOptions = [];

    this._qvOverlay?.addEventListener('click', () => this._closeQuickView());

    // Delegated listeners attached to `document` — the most resilient way to
    // catch events no matter how many custom elements are nested or what other
    // code might stopPropagation along the way. Each handler checks whether
    // the event target lives inside THIS quickview's content before acting.
    document.addEventListener('click', this._handleContentClick, true);
    document.addEventListener('change', this._handleContentChange, true);
    console.log('[SF-QV][4] click + change listeners attached to document (capture)');

    this._onKeyDown = (e) => {
      if (e.key === 'Escape') this._closeQuickView();
    };
    document.addEventListener('keydown', this._onKeyDown);

    let resizeTimer;
    this._onResize = () => {
      if (this._qvDrawer) {
        this._qvDrawer.style.transition = 'none';
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          this._qvDrawer.style.transition = '';
        }, 300);
      }
    };
    window.addEventListener('resize', this._onResize);
  }

  _openQuickView(handle, productUrl, variantId) {
    if (!this._quickview) return;
    this._quickview.setAttribute('aria-hidden', 'false');
    this._quickview.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    this._qvContent.innerHTML = '';
    this._qvLoading?.classList.add('is-active');

    let fetchUrl = `/products/${handle}?sections=ec-quickview-product`;
    if (variantId) fetchUrl += `&variant=${encodeURIComponent(variantId)}`;

    fetch(fetchUrl)
      .then((res) => {
        if (!res.ok) throw new Error('Fetch failed');
        return res.json();
      })
      .then((data) => {
        const html = data['ec-quickview-product'];
        if (!html) throw new Error('Section returned null');
        this._qvContent.innerHTML = html;
        this._qvLoading?.classList.remove('is-active');
        this._initQuickViewInteractions(productUrl, variantId);
        this._reloadStampedBadges();
      })
      .catch(() => {
        this._qvLoading?.classList.remove('is-active');
        this._qvContent.innerHTML =
          '<div class="ec-qv__panel" style="justify-content:center;align-items:center">' +
          '<p style="padding:16px;font-family:Figtree,sans-serif;font-size:14px;color:#555">' +
          'Could not load product. <a href="' + productUrl + '" style="color:#1f1f1f;text-decoration:underline">View full details</a></p></div>';
      });
  }

  _initQuickViewInteractions(productUrl, variantId) {
    const content = this._qvContent;

    const qvImages = content.querySelector('.ec-qv__images');
    const qvIndicator = content.querySelector('.ec-qv__progress-indicator');
    if (qvImages && qvIndicator) {
      const updateQvProgress = () => {
        const maxScroll = qvImages.scrollWidth - qvImages.clientWidth;
        if (maxScroll <= 0) {
          qvIndicator.style.width = '100%';
          qvIndicator.style.left = '0%';
          return;
        }
        const track = qvIndicator.parentElement;
        const trackW = track.clientWidth;
        const imageCount = qvImages.querySelectorAll('.ec-qv__image-item').length;
        let indicatorW;
        if (imageCount === 2) {
          indicatorW = 30;
        } else {
          const visibleRatio = qvImages.clientWidth / qvImages.scrollWidth;
          const indicatorPx = Math.min(Math.max(visibleRatio * trackW, trackW * 0.08), 42);
          indicatorW = (indicatorPx / trackW) * 100;
        }
        const progress = maxScroll > 0 ? qvImages.scrollLeft / maxScroll : 0;
        qvIndicator.style.width = indicatorW + '%';
        qvIndicator.style.left = progress * (100 - indicatorW) + '%';
      };
      qvImages.addEventListener('scroll', updateQvProgress, { passive: true });
      updateQvProgress();
      // Mobile swipe is handled by native CSS: overflow-x:auto + scroll-snap.
      // Matches sf-ugc__carousel — browser momentum feels fluid, JS cannot match it.
    }

    // Refresh per-open state used by the class-level delegated click listener.
    const jsonEl = content.querySelector('script[data-ec-qv-product]');
    this._productData = null;
    if (jsonEl) {
      try { this._productData = JSON.parse(jsonEl.textContent); } catch (e) { /* noop */ }
    }
 
    this._selectedOptions = [];
    if (this._productData) {
      const selectedVariant = this._productData.variants.find((v) => v.id === this._productData.selected_variant_id);
      if (selectedVariant) {
        selectedVariant.options.forEach((opt) => this._selectedOptions.push(opt));
      }
    }

    // On open, the stack stays at its natural top (first item — typically the
    // video when one exists). Scroll-to-variant fires only on variant click.
  }

  // Only act when this instance's drawer is the one currently open. With a
  // single open quickview on the page at a time this avoids duplicate handling
  // when multiple <sf-collection-quickview> instances coexist.
  _isActive() {
    return this._quickview instanceof Element && this._quickview.classList.contains('is-open');
  }

  _handleContentClick = (ev) => {
    const target = ev.target;
    if (!(target instanceof Element)) return;
    const content = target.closest('.ec-quickview__content');
    if (!content || !this._isActive()) return;

    const variantBtn = target.closest('.ec-qv__swatch, .ec-qv__size, .ec-qv__option-btn');
    if (variantBtn) {
      this._handleVariantSelect(variantBtn);
      return;
    }

    if (target.closest('.ec-qv__close')) {
      this._closeQuickView();
      return;
    }

    const addBtn = target.closest('.ec-qv__add-to-bag');
    if (addBtn) {
      const variantId = addBtn.dataset.variantId;
      if (!variantId || addBtn.disabled) return;
      this._qvAddToCart(addBtn, variantId);
      return;
    }

    if (target.closest('.ec-qv__save')) {
      if (this._productData) {
        this.dispatchEvent(new CustomEvent('wishlist:add', {
          bubbles: true,
          detail: { productId: this._productData.id, url: this._productData.url }
        }));
      }
    }
  };

  _handleVariantSelect(btn) {
    const idx = parseInt(btn.dataset.optionIndex, 10);
    const value = btn.dataset.value;
    const parent = btn.closest('.ec-qv__option');

    parent?.querySelectorAll('.ec-qv__swatch, .ec-qv__size, .ec-qv__option-btn').forEach((b) => {
      b.classList.remove('is-selected');
    });
    btn.classList.add('is-selected');

    const selectedLabel = parent?.querySelector('[data-qv-option-selected]');
    if (selectedLabel) selectedLabel.textContent = value.toUpperCase();

    this._selectedOptions[idx] = value;
    if (this._productData) {
      const variant = this._productData.variants.find((v) =>
        v.options.every((opt, i) => opt === this._selectedOptions[i])
      );
      if (variant) this._updateQuickViewVariant(variant);
    }
  }

  _handleContentChange = (ev) => {
    const target = ev.target;
    console.log('[SF-QV][5] change event', { target, type: target?.type });
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') {
      console.log('[SF-QV][5x] not a radio input, skipping');
      return;
    }
    const content = target.closest('.ec-quickview__content');
    console.log('[SF-QV][6] change inside content?', !!content, 'instance active?', this._isActive());
    if (!content || !this._isActive()) return;

    const option = target.closest('.variant-dropdown__option');
    console.log('[SF-QV][7] option found?', !!option, option);
    if (!option) return;

    this._handleDropdownSelect(target, option);
  };

  _handleDropdownSelect(input, option) {
    const idx = parseInt(input.dataset.optionIndex, 10);
    const value = input.dataset.value || input.value;
    const dropdown = option.closest('.variant-dropdown');
    const parent = option.closest('.ec-qv__option');
    console.log('[SF-QV][8] _handleDropdownSelect', { idx, value, hasDropdown: !!dropdown, hasProductData: !!this._productData });
    if (!dropdown) return;

    // Update selected class on the label (CSS `:has(input:checked)` also works
    // but explicit class keeps parity with other styles)
    dropdown.querySelectorAll('.variant-dropdown__option').forEach((el) => el.classList.remove('is-selected'));
    option.classList.add('is-selected');

    const trigger = dropdown.querySelector('.variant-dropdown__trigger');
    console.log(trigger);
    const triggerText = trigger?.querySelector('.variant-dropdown__trigger-text');
    if (triggerText) triggerText.textContent = value;
    if (trigger && input.dataset.valueHandle) trigger.dataset.valueHandle = input.dataset.valueHandle;

    dropdown.removeAttribute('open');

    const selectedLabel = parent?.querySelector('[data-qv-option-selected]');
    if (selectedLabel) selectedLabel.textContent = value.toUpperCase();

    this._selectedOptions[idx] = value;
    if (this._productData) {
      const variant = this._productData.variants.find((v) =>
        v.options.every((opt, i) => opt === this._selectedOptions[i])
      );
      if (variant) this._updateQuickViewVariant(variant);
    }
  }

  _updateQuickViewVariant(variant) {
    const content = this._qvContent;

    if (variant.featured_media_id != null) this._scrollToVariantMedia(variant.featured_media_id, 'smooth');

    const priceEl = content.querySelector('[data-qv-price]');
    if (priceEl) {
      let priceHtml = '';
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        priceHtml += '<span class="ec-qv__compare-price">' + variant.compare_at_price_formatted + '</span>';
      }
      priceHtml += '<span class="ec-qv__current-price">' + variant.price_formatted + '</span>';
      priceEl.innerHTML = priceHtml;
    }

    const addBtn = content.querySelector('.ec-qv__add-to-bag');
    if (addBtn) {
      addBtn.dataset.variantId = variant.id;
      if (variant.available) {
        addBtn.disabled = false;
        addBtn.innerHTML = 'ADD TO BAG <span class="ec-qv__bag-plus">[+]</span>';
      } else {
        addBtn.disabled = true;
        addBtn.innerHTML = 'SOLD OUT <span class="ec-qv__bag-plus">[+]</span>';
      }
    }
  }

  // Scrolls the media stack so the item with matching media id is in view.
  // Mirrors #handleVariantUpdate in sf-product-media-scroll.js.
  _scrollToVariantMedia(mediaId, behavior = 'smooth') {
    if (!this._qvContent || mediaId == null) return;
    const target = this._qvContent.querySelector(
      `.ec-qv__image-item[data-media-id="${CSS.escape(String(mediaId))}"]`
    );
    if (!target) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({
      behavior: reduceMotion ? 'auto' : behavior,
      block: 'nearest',
      inline: 'nearest'
    });
  }

  _qvAddToCart(btn, variantId) {
    const originalHTML = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = 'ADDING... <span class="ec-qv__bag-plus">[+]</span>';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
    })
      .then((res) => {
        if (!res.ok) throw new Error('Cart add failed');
        return res.json();
      })
      .then(() => {
        btn.innerHTML = 'ADDED ✓ <span class="ec-qv__bag-plus">[+]</span>';
        this._closeQuickView();
        return fetch('/cart.js').then((r) => r.json());
      })
      .then((cart) => {
        if (typeof window.SLIDECART_UPDATE === 'function') {
          window.SLIDECART_UPDATE();
          if (typeof window.SLIDECART_OPEN === 'function') {
            window.SLIDECART_OPEN();
          }
        }

        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: cart, data: { itemCount: cart.item_count } }
        }));

        setTimeout(() => {
          btn.disabled = false;
          btn.innerHTML = originalHTML;
        }, 2000);
      })
      .catch(() => {
        btn.disabled = false;
        btn.innerHTML = originalHTML;
      });
  }

  _closeQuickView() {
    if (!this._quickview) return;
    this._quickview.classList.remove('is-open');
    this._quickview.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  _reloadStampedBadges() {
    const badges = this._qvContent?.querySelectorAll('.stamped-product-reviews-badge');
    if (!badges || !badges.length) return;

    badges.forEach((badge) => {
      const productId = badge.getAttribute('data-id');
      if (!productId) return;

      const sourceSelector = `.stamped-product-reviews-badge[data-id="${CSS.escape(productId)}"]`;
      const candidates = document.querySelectorAll(sourceSelector);
      let source = null;
      for (const el of candidates) {
        if (el === badge) continue;
        if (this._qvContent?.contains(el)) continue;
        if (el.innerHTML.trim().length > 0 && el.querySelector('.stamped-badge, .stamped-fa-star, .stamped-badge-starrating')) {
          source = el;
          break;
        }
      }

      if (source) {
        badge.innerHTML = source.innerHTML;
        return;
      }

      this._stampedApiReload();
    });

    this._stampedApiReload();
  }

  _stampedApiReload() {
    const tryReload = () => {
      const fn = window.StampedFn;
      if (fn && typeof fn.reloadBadges === 'function') { fn.reloadBadges(); return true; }
      if (fn && typeof fn.loadBadges === 'function') { fn.loadBadges(); return true; }
      if (fn && typeof fn.init === 'function') { fn.init(); return true; }

      const s = window.Stamped || window.stamped;
      if (s && typeof s.reloadBadges === 'function') { s.reloadBadges(); return true; }
      if (s && typeof s.loadBadges === 'function') { s.loadBadges(); return true; }
      if (s && typeof s.init === 'function') { s.init(); return true; }

      if (window.stampedJS && typeof window.stampedJS.reload === 'function') {
        window.stampedJS.reload();
        return true;
      }
      return false;
    };

    if (tryReload()) return;

    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      if (tryReload() || attempts >= 20) clearInterval(interval);
    }, 250);
  }
}

if (!customElements.get('sf-collection-quickview')) {
  customElements.define('sf-collection-quickview', SfCollectionQuickview);
}

/* ─────────────────────────────────────────────────────────────────────────
   Safety net — bare document listeners that run even if the custom element
   fails to upgrade. They only touch DOM inside an open .ec-quickview and
   match what the class-level handlers do. Attached ONCE.
   ───────────────────────────────────────────────────────────────────────── */
(() => {
  if (window.__sfQuickviewSafetyNetBound) return;
  window.__sfQuickviewSafetyNetBound = true;

  const openQv = () =>
    document.querySelector('.ec-quickview.is-open');

  const findProductData = (qv) => {
    const jsonEl = qv.querySelector('script[data-ec-qv-product]');
    if (!jsonEl) return null;
    try { return JSON.parse(jsonEl.textContent); } catch { return null; }
  };

  const findSelectedOptions = (qv, productData) => {
    // Build from the CURRENT UI state (checked radios + selected swatches/sizes).
    if (!productData) return [];
    const selected = [];
    productData.variants[0]?.options.forEach((_, i) => (selected[i] = undefined));

    // Walk each option group and take whatever is selected.
    qv.querySelectorAll('.ec-qv__option').forEach((opt) => {
      const idxAttr = opt.getAttribute('data-option-index');
      const idx = idxAttr != null ? parseInt(idxAttr, 10) : NaN;
      if (Number.isNaN(idx)) return;

      const checkedRadio = opt.querySelector('.variant-dropdown__option input[type="radio"]:checked');
      if (checkedRadio) {
        selected[idx] = checkedRadio.dataset.value || checkedRadio.value;
        return;
      }
      const selectedBtn = opt.querySelector('.ec-qv__swatch.is-selected, .ec-qv__size.is-selected, .ec-qv__option-btn.is-selected');
      if (selectedBtn) selected[idx] = selectedBtn.dataset.value;
    });

    // Fill in from the initial selected variant for any option that has nothing.
    const initial = productData.variants.find((v) => v.id === productData.selected_variant_id);
    if (initial) initial.options.forEach((val, i) => { if (selected[i] == null) selected[i] = val; });

    return selected;
  };

  const updateVariant = (qv, variant) => {
    if (!variant) return;
    const priceEl = qv.querySelector('[data-qv-price]');
    if (priceEl) {
      let html = '';
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        html += `<span class="ec-qv__compare-price">${variant.compare_at_price_formatted}</span>`;
      }
      html += `<span class="ec-qv__current-price">${variant.price_formatted}</span>`;
      priceEl.innerHTML = html;
    }
    const addBtn = qv.querySelector('.ec-qv__add-to-bag');
    if (addBtn) {
      addBtn.dataset.variantId = variant.id;
      if (variant.available) {
        addBtn.disabled = false;
        addBtn.innerHTML = 'ADD TO BAG <span class="ec-qv__bag-plus">[+]</span>';
      } else {
        addBtn.disabled = true;
        addBtn.innerHTML = 'SOLD OUT <span class="ec-qv__bag-plus">[+]</span>';
      }
    }
    if (variant.featured_media_id != null) {
      const target = qv.querySelector(
        `.ec-qv__image-item[data-media-id="${CSS.escape(String(variant.featured_media_id))}"]`
      );
      if (target) {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({
          behavior: reduceMotion ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });
      }
    }
  };

  // Dropdown radio change — close details, update trigger, swap variant.
  document.addEventListener('change', (ev) => {
    const input = ev.target;
    console.log('[SF-QV-SAFETY] change event', { target: input });
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    const option = input.closest('.variant-dropdown__option');
    console.log('[SF-QV-SAFETY] option?', !!option);
    if (!option) return;
    const qv = input.closest('.ec-quickview.is-open');
    console.log('[SF-QV-SAFETY] inside open quickview?', !!qv);
    if (!qv) return;

    console.log('[SF-QV-SAFETY] processing dropdown selection');

    const dropdown = input.closest('.variant-dropdown');
    dropdown?.querySelectorAll('.variant-dropdown__option').forEach((o) => o.classList.remove('is-selected'));
    option.classList.add('is-selected');

    const value = input.dataset.value || input.value;
    const trigger = dropdown?.querySelector('.variant-dropdown__trigger');
    const triggerText = trigger?.querySelector('.variant-dropdown__trigger-text');
    if (triggerText) triggerText.textContent = value;
    if (trigger && input.dataset.valueHandle) trigger.dataset.valueHandle = input.dataset.valueHandle;

    dropdown?.removeAttribute('open');

    const parent = option.closest('.ec-qv__option');
    const selectedLabel = parent?.querySelector('[data-qv-option-selected]');
    if (selectedLabel) selectedLabel.textContent = value.toUpperCase();

    const productData = findProductData(qv);
    if (!productData) return;
    const selectedOptions = findSelectedOptions(qv, productData);
    const match = productData.variants.find((v) => v.options.every((opt, i) => opt === selectedOptions[i]));
    if (match) updateVariant(qv, match);
  }, false);

  // Add-to-bag inside any open quickview, so it works even if the element
  // didn't bind. Uses /cart/add.js then triggers the slidecart helpers.
  document.addEventListener('click', (ev) => {
    const t = ev.target instanceof Element ? ev.target : null;
    if (!t) return;
    const qv = t.closest('.ec-quickview.is-open');
    if (!qv) return;

    const close = t.closest('.ec-qv__close');
    if (close) {
      qv.classList.remove('is-open');
      qv.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      return;
    }

    const overlay = t.closest('.ec-quickview__overlay');
    if (overlay) {
      qv.classList.remove('is-open');
      qv.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      return;
    }
  }, false);
})();
